import { useEffect, useState } from "react";
import { useMatches } from "react-router-dom";

import RouterTags from "../../../constants/RouterTags";

/**
 * The id of the review report the viewer is currently filling out, or null when they aren't.
 *
 * `/review/:reviewID/` renders the legacy template for every audience — author, signer, admin,
 * read-only viewer — so the route alone can't tell them apart. review.html stamps both the
 * authoring state (the same conditions form_body.html uses to choose inputs over read-only answers)
 * and the report id onto the breadcrumbs mount, which we read once the template DOM is in place.
 * The id travels to the coach agent, which re-checks authorship server-side before loading anything.
 */
export const useAuthoringReviewReportId = (): string | null => {
	const matches = useMatches();
	const isOnReviewFormPage = matches.some((m) =>
		((m.handle as { tags?: string[] } | undefined)?.tags ?? []).includes(RouterTags.FORM_AUTHORING)
	);

	const [reviewReportId, setReviewReportId] = useState<string | null>(null);

	useEffect(() => {
		if (!isOnReviewFormPage) {
			setReviewReportId(null);
			return;
		}
		const mount = document.getElementById("form-authoring-breadcrumbs");
		const isAuthoring = mount?.dataset.isAuthoring === "true";
		setReviewReportId(isAuthoring ? (mount?.dataset.reviewReportId ?? null) : null);
	}, [isOnReviewFormPage]);

	return isOnReviewFormPage ? reviewReportId : null;
};

/** Whether the viewer is filling out a review form, rather than just reading one. */
export const useIsAuthoringReviewForm = (): boolean => useAuthoringReviewReportId() !== null;

/**
 * The id of the form the viewer currently has open in the form builder, or null when they
 * aren't on that route.
 *
 * Unlike `useAuthoringReviewReportId`, the form builder is a plain React-Router route, so
 * the id comes straight from the matched route's params — no DOM bridge needed.
 */
export const useCurrentFormBuilderFormId = (): string | null => {
	const matches = useMatches();
	const formBuilderMatch = matches.find((m) =>
		((m.handle as { tags?: string[] } | undefined)?.tags ?? []).includes(RouterTags.FORM_BUILDER)
	);
	return (formBuilderMatch?.params as { baseFormId?: string } | undefined)?.baseFormId ?? null;
};
